import qs from 'query-string';

import { StorageType, Store } from '@nakedplatform/support';

let userConfigured = false;

const UTM_HEADERS: UtmKeys[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'wbraid',
];

/**
 * Maps the UTM headers to touched headers
 */
function getTouchedHeaders() {
  return UTM_HEADERS.map(header => `last_touch_${header}`);
}
/**
 * Creates an object of the query string parameters
 */
export function getQueryParams() {
  return qs.parse(window.location.search);
}
/**
 * Retrieves UTM Parameters from search
 */
export function getUtmParams() {
  let storageParams = {};

  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);

    storageParams = Store.getItems(storage, [...UTM_HEADERS, ...getTouchedHeaders()]);
  }

  const params = getQueryParams();
  const paramsFirstTouch: Partial<UtmHeaders> | qs.ParsedQuery<string> = {};
  const paramsLastTouch: Partial<UtmHeaders> | qs.ParsedQuery<string> = {};

  UTM_HEADERS.forEach(header => {
    paramsLastTouch[`last_touch_${header}` as UtmKeys] =
      params[header] ?? storageParams[`last_touch_${header}`] ?? 'none';
    paramsFirstTouch[header] = storageParams[header] ?? params[header] ?? 'none';
  });

  return {
    paramsFirstTouch,
    paramsLastTouch,
  };
}
/**
 * Retrieves UTM Parameters from local storage
 */
export function getLocalUtmParams() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);
    const headers = getTouchedHeaders();
    const paramsFirstTouch = Store.getItems(storage, UTM_HEADERS, 'none');
    const paramsLastTouch = Store.getItems(storage, headers, 'none');

    return {
      paramsFirstTouch,
      paramsLastTouch,
    };
  }

  return {
    paramsFirstTouch: {},
    paramsLastTouch: {},
  };
}
/**
 * Configure user from current location
 */
export function setupUser() {
  if (userConfigured) {
    return;
  }

  userConfigured = true;
  const { paramsFirstTouch, paramsLastTouch } = getUtmParams();
  const currentDate = new Date().toISOString();
  const firstSeen = {
    'First Seen Date': currentDate,
    'First Seen on Web Date': currentDate,
    'First Seen Source': 'WEB',
  };

  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);

    Store.setItems(storage, paramsFirstTouch as never, true);
    Store.setItems(storage, paramsLastTouch as never);
    Store.setItems(storage, firstSeen, true);
  }

  if (window.analytics) {
    window.analytics.identify(paramsLastTouch);

    window.analytics.ready(() => {
      window.mixpanel.register({
        source: 'WEB',
      });
      window.mixpanel.register_once(paramsFirstTouch);
      window.mixpanel.register_once(firstSeen);
      window.mixpanel.people.set_once(firstSeen);
      window.mixpanel.people.set_once(paramsFirstTouch);
    });
  }
}
/* Type definitions */
type UtmKeys =
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_term'
  | 'utm_content'
  | 'gclid'
  | 'fbclid'
  | 'wbraid'
  | 'last_touch_utm_source'
  | 'last_touch_utm_medium'
  | 'last_touch_utm_campaign'
  | 'last_touch_utm_term'
  | 'last_touch_utm_content'
  | 'last_touch_gclid'
  | 'last_touch_fbclid'
  | 'last_touch_wbraid';

type UtmHeaders = Record<UtmKeys, string>;
